import { Communication } from "neo-games";

type IReel = Communication.Game.IReel;

export interface ICheckOptions {
    force: boolean;
    stoppedSymbols: Array<IReel>;
}

export interface IReelTeasingAttributes {
    action: number;
    stopping: boolean;
    stopDelay?: number;
}

export interface ISymbolTeasingAttributes {
    teasable: boolean;
    action?: number;
}

export const ReelTeasingActions = {
    DO_NOTHING: 0,
    SPIN_STOP: 1,
    TEASE: 2,
    FORCE_SPIN_STOP: 3, // todo Use this?
};

export const SymbolTeasingActions = {
    NOWIN: 0,
    NORMAL: 1,
    IMPACT: 2,
    TEASE: 3,
    IMPACT_TEASE: 4,
    EMPTY: 5,
};

/**
 * @author Marek Turkovic
 */
export abstract class TeasingComponentAbstract {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    // count of reel
    protected reelCount: number;
    // number of symbols which will be visible on screen during the rotation
    protected _visibleSymbolCount: number;
    // reels x positions
    protected reelX: Array<number>;
    // reels y positions
    protected reelY: Array<number>;
    // delay to stop rotation in frames
    protected stopDelay: number;
    // teased symbol
    protected teasingSymbol: number;
    // delay to stop teasing rotation in frames
    protected teasingStopDelay: number;
    protected minimumSymbolsForTeasing: number;
    protected countSymbolsOnReelsAndRows: number;

    protected canPlayTeaserSound: boolean;
    public wasPlayedTeaserSound: boolean;
    protected wasPlayedTeaserWinSound: boolean;
    protected wasPlayedTeaserFailSound: boolean;

    protected elementInstance: Array<Object> = [];

    // array of stopped symbols for finish reel rotation
    public stoppedSymbolsArray: Array<IReel>;

    // teasing enabled in current round ?
    public teasingEnabled: boolean = false; // todo create getter for public access ?
    protected force: boolean = false;
    public reelsTeasingAttributes: Array<IReelTeasingAttributes> = [];
    public symbolsTeasingAttributes: Array<Array<ISymbolTeasingAttributes>> = [];

    protected _customData: any;

    /**
     * Setting customData.
     *
     * Example: this.teasingMystery.customData = {'selectedSymbol': GameManager.freespinsResponse.customData['selectedSymbol']};
     *
     * @param value
     */
    set customData(value: any) {
        this._customData = value;
    }

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    /**
     *
     * @param {Element} node
     */
    public create(node: Element): void {}

    //******************************************************************************************************************
    //  CONFIGURATE
    //******************************************************************************************************************

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    protected configurate(node: Element): void {}

    /**
     *
     * @param {Object} element
     */
    public addElementInstance(element: Object): void {}

    //******************************************************************************************************************
    //  CHECK REELS
    //******************************************************************************************************************

    /**
     * Check called before reel.spinStop(). Checks if teasing is possible and checks which reel can be stopped immediatly.
     * @param {ICheckOptions} options
     */
    abstract checkOnInit(options: ICheckOptions): Array<IReelTeasingAttributes>;

    /**
     * Counts teasing symbols on reels [0..reelIndex] and decide if it has a sense to start teasing.
     * @returns {boolean}
     * @param reelIndexStart
     * @param reelIndexStop
     */
    protected abstract isEnoughtForStartTeasing(reelIndexStart: number, reelIndexStop: number): boolean;

    /**
     * Count symbols of some kind on reels.
     * @param {number} reelIndexStart
     * @param {number} reelIndexStop
     * @param symbolId
     * @returns {number}
     */
    abstract countSymbols(reelIndexStart: number, reelIndexStop: number, symbolId: number): number;

    /**
     *
     * @param {number} reelIndexStart
     * @param {number} reelIndexStop
     * @param {number} symbolId
     * @returns {number}
     */
    protected abstract getCountSymbolsOnReelsAndRows(
        reelIndexStart: number,
        reelIndexStop: number,
        symbolId: number,
    ): number;

    //******************************************************************************************************************
    //  CHECK SYMBOLS TEASING
    //******************************************************************************************************************

    /**
     *
     * @param {number} reelIndex
     * @returns {Array<ISymbolTeasingAttributes>}
     */
    abstract checkSymbolsPerReel(reelIndex: number): Array<Array<ISymbolTeasingAttributes>>;

    //******************************************************************************************************************
    //  REELS TEASING ANIMATIONS
    //******************************************************************************************************************

    /**
     * Start animating teasing layers.
     *
     * @param {number} reelIndex
     */
    abstract animateTeasing(reelIndex: number): void;

    /**
     *
     */
    abstract finishTeasing(): void;

    /**
     * Remove teasing animations
     *
     * @param {boolean} noWin
     */
    abstract removeTeasing(noWin: boolean): void;

    /**
     * Start animating reel layer.
     *
     * @param {number} reelIndex
     */
    abstract reelAnimationStart(reelIndex: number): void;

    /**
     * Stop animating reel layer.
     */
    abstract reelAnimationStop(): void;

    //******************************************************************************************************************
    //  LOG
    //******************************************************************************************************************

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {}
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/TeasingComponentAbstract.ts
 **/